<template>
    <PageLoading :loading="loading">
        <!-- 错误页面 -->
        <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />

        <!-- 正常页面 -->
        <div class="profile-completion" v-else>
            <!-- 基本信息 -->
            <div class="info-section">
                <div class="section-title margin-bottom">基本信息</div>
                <div class="info-item info-content">
                    <div>
                        <div class="info-label">姓名</div>
                        <span class="info-value">{{ user.name || '未设置' }}</span>
                    </div>
                    <div class="edit-action" @click="editName">
                        <van-icon name="edit" color="#8B0000" />
                        <span class="edit-text">修改</span>
                    </div>
                </div>
                <div class="info-item border-top info-content">
                    <div>
                        <div class="info-label">用户ID</div>
                        <span class="info-value">{{ user.wx_user_code || '未设置' }}</span>
                    </div>
                    <span class="readonly-text">不可修改</span>
                </div>
            </div>

            <!-- 限购设置 -->
            <div class="info-section">
                <div class="section-header">
                    <div class="section-title">限购设置</div>
                    <div class="edit-action" @click="editPurchaseLimits">
                        <van-icon name="edit" color="#8B0000" />
                        <span class="edit-text">修改</span>
                    </div>
                </div>
                <div class="info-item">
                    <div class="info-label">学校限购次数</div>
                    <div class="info-content">
                        <span class="info-value">{{ schoolLimitText }}</span>
                    </div>
                </div>
                <div class="info-item border-top">
                    <div class="info-label">社区限购次数</div>
                    <div class="info-content">
                        <span class="info-value">{{ communityLimitText }}</span>
                    </div>
                </div>
            </div>

            <!-- 人脸信息 -->
            <div class="info-section">
                <div class="section-title margin-bottom">人脸信息</div>
                <div class="face-info-cell">
                    <!-- 已录入人脸状态 -->
                    <div v-if="hasFaceInfo" class="face-recorded">
                        <div class="face-info">
                            <van-icon :name="require('@/assets/icons/success.svg')" class="status-icon" />
                            <div class="face-status">
                                <span class="status-text">已录入人脸</span>
                                <div class="face-instruction">可正常使用刷脸功能</div>
                            </div>
                        </div>
                        <van-button class="face-delete-btn" plain round @click="deleteFace">
                            <van-icon name="delete" color="#EF4444" class="delete-icon" />
                            <span>删除</span>
                        </van-button>
                    </div>

                    <!-- 未录入人脸状态 -->
                    <div v-else class="face-not-recorded">
                        <div class="face-info">
                            <van-icon :name="require('@/assets/icons/warn.svg')" class="status-icon" />
                            <div class="face-status">
                                <span class="status-text">未录入人脸信息</span>
                                <div class="face-instruction">请点击下方录入人脸，进行脸部信息录入。</div>
                            </div>
                        </div>
                        <van-button class="face-record-btn" type="primary" round @click="recordFace">
                            录入人脸
                        </van-button>
                    </div>
                </div>
            </div>

            <!-- 修改姓名弹框 -->
            <van-dialog v-model:show="showNameDialog" title="修改姓名" show-cancel-button @confirm="confirmNameChange"
                @cancel="cancelNameChange" class="name-dialog">
                <van-field v-model="tempName" placeholder="请输入姓名" class="name-field" />
            </van-dialog>

            <!-- 修改限购设置弹框 -->
            <DailyLimitModal v-model:show="showLimitsDialog" :student-data="user" @submit="handleLimitSubmit" />
        </div>
    </PageLoading>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import { setWxTitle } from '@/utils/wx'
import { post } from '@/utils/request'
import { Toast, Dialog } from 'vant'
import DailyLimitModal from '@/components/DailyLimitModal.vue'
import { WECHAT_APPID } from '../../../config/env'
import PageLoading from '@/components/PageLoading.vue'
import ErrorPage from '@/components/ErrorPage.vue'

// 用户信息
const user = ref({})

// 加载状态
const loading = ref(true)
// 错误状态
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')

// 处理错误
const handleError = (title, message) => {
    hasError.value = true
    errorTitle.value = title
    errorMessage.value = message
    loading.value = false
}

// 弹框状态
const showNameDialog = ref(false)
const showLimitsDialog = ref(false)
const tempName = ref('')

// 计算属性
const hasFaceInfo = computed(() => {
    return Object.keys(user.value.face_image_arr || {}).length > 0
})

const schoolLimitText = computed(() => {
    return `每日最多${user.value.school_quota_num}次`
})

const communityLimitText = computed(() => {
    return `每日最多${user.value.quota_num}次`
})

onMounted(() => {
    setWxTitle('完善本人资料')
    initPage()
})

// 页面初始化
const initPage = async () => {
    try {
        // 这里可以调用接口获取用户信息数据
        const res = await post('/api/index/info', {
            page_type: '个人中心',
            app_id: WECHAT_APPID,
            partner_id: parseInt(localStorage.getItem('userId')),
        })
        if (res.result && res.result.state === 'ok') {
            user.value = res.result.partner_info
        } else {
            handleError('加载失败', res.result?.message || '接口异常')
        }
    } catch (error) {
        console.error('页面初始化失败:', error)
        handleError('加载失败', '页面加载失败，请重试')
    } finally {
        loading.value = false
    }
}

// 编辑姓名
const editName = () => {
    tempName.value = user.value.name || ''
    showNameDialog.value = true
}

// 确认修改姓名
const confirmNameChange = async () => {
    if (!tempName.value.trim()) {
        Toast('请输入姓名')
        return
    }

    try {
        // 这里调用修改姓名的API
        const res = await post('/api/update/partner/info', {
            name: tempName.value.trim(),
            partner_id: user.value.id
        })

        if (res.result && res.result.state === 'ok') {
            user.value.name = tempName.value.trim()
            Toast('修改成功')
        } else {
            Dialog.alert({
                title: '修改失败',
                message: res.result?.message || '修改失败'
            })
        }
    } catch (error) {
        console.error('修改姓名失败:', error)
        Toast('修改失败，请重试')
    }
}

// 取消修改姓名
const cancelNameChange = () => {
    tempName.value = ''
}

// 编辑限购设置
const editPurchaseLimits = () => {
    user.value.from_page = 'profile-completion'
    showLimitsDialog.value = true
}

// 处理限购设置提交
const handleLimitSubmit = (studentData) => {
    // console.log('studentData', studentData)
    user.value.school_quota_num = studentData.school_quota_num
    user.value.quota_num = studentData.quota_num
}

// 录入人脸
const recordFace = () => {
    // 跳转到人脸录入页面
    window.location.href = '/face-recognition'
}

// 删除人脸
const deleteFace = () => {
    Dialog.confirm({
        title: '确认',
        message: '确定要删除吗？',
    }).then(async () => {
        // 用户确认删除
        try {
            // 这里调用删除人脸的API
            const res = await post('/api/update/partner/info', {
                partner_id: user.value.id,
                name: user.value.name,
                is_delete_face: '是'
            })

            if (res.result && res.result.state === 'ok') {
                // 更新用户信息，清除人脸数据
                user.value.face_image_arr = {}
                Toast('删除成功')
            } else {
                Dialog.alert({
                    title: '删除失败',
                    message: res.result?.message || '删除失败'
                })
            }
        } catch (error) {
            console.error('删除人脸失败:', error)
            Toast('删除失败，请重试')
        }
    }).catch(() => {
        // 用户取消删除，不做任何操作
        console.log('用户取消删除人脸')
    })
}
</script>

<style scoped>
.profile-completion {
    background: #FFFFFF;
    padding: 14px;
    height: calc(100vh - 28px);
}

.info-section {
    margin-bottom: 10px;
    padding: 11px;
    border-radius: 7px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.section-title {
    font-size: 16px;
    font-weight: 500;
    color: #000000;
}

.margin-bottom {
    margin-bottom: 11px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 11px;
}

.info-item {
    margin-bottom: 11px;
    padding: 8px 0;
}

.info-item:last-child {
    margin-bottom: 0;
}

.info-content,
.face-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.info-item.border-top {
    border-top: 1px solid #F3F4F6;
}

.info-label {
    font-size: 14px;
    font-weight: 500;
    color: #000000;
    margin-bottom: 2px;
}

.info-value {
    font-size: 12px;
    color: #6B7280;
}

.edit-action {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
}

.edit-text {
    font-size: 12px;
    color: var(--van-primary-color);
}

.readonly-text {
    font-size: 12px;
    color: #9CA3AF;
}

.face-recorded,
.face-not-recorded {
    display: flex;
    flex-direction: column;
    align-items: start;
}

.status-icon {
    font-size: 30px;
}

.status-text {
    font-size: 14px;
    color: #000000;
}

.face-instruction {
    margin-top: 3px;
    font-size: 13px;
    color: #9CA3AF;
}

.face-info {
    margin-bottom: 17px;
    gap: 10px;
}

.face-record-btn {
    background: #16A34A;
    border: none;
    width: 177px;
    height: 36px;
    font-size: 14px;
}

.face-delete-btn {
    border: 1px solid #EF4444;
    color: #EF4444;
    background: #fff;
    width: 177px;
    height: 36px;
    font-size: 14px;
}

.delete-icon {
    font-size: 15px;
    margin-right: 4px;
}

/* 修改姓名弹框样式 */
:deep(.name-dialog .van-dialog) {
    border-radius: 8px;
    border: 1px solid #E5E7EB;
}

:deep(.name-field .van-field__control) {
    border: 1px solid #E5E7EB;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 14px;
}

:deep(.name-field .van-field__control:focus) {
    border-color: var(--van-primary-color);
}
</style>
